<template>
  <div>
    <o-card>
      <template slot="header">
        <h4>{{ $t('sandoz.product_master_matrix') }}</h4>
      </template>
      <div class="mb-3">
        <b-row>
          <b-col cols="2">
            <label>{{ $t('sandoz.year') }}</label>
            <b-form-select v-model="selectedYear" @change="onYearChange">
              <option v-for="year in years" :key="year.value" :value="year.value">
                {{ year.text }}
              </option>
            </b-form-select>
          </b-col>
          <b-col cols="10">
            <label>
              {{ $t('sandoz.contract_types') }} (
              <b-form-checkbox
                v-if="availableSalesLines.length > 0"
                v-model="allContractTypesSelected"
                @change="toggleAllContractTypes"
                class="d-inline-block"
                inline
              >
                {{ $t('sandoz.select_all') }}
              </b-form-checkbox>
              )
            </label>
            <div v-if="availableSalesLines.length > 0" class="border p-2" style="max-height: 150px; overflow-y: auto;">
              <b-form-checkbox-group v-model="selectedContractTypes" class="d-flex flex-wrap">
                <b-form-checkbox
                  v-for="salesLine in availableSalesLines"
                  :key="salesLine.id"
                  :value="salesLine.id"
                  class="mr-3 mb-1"
                >
                  {{ salesLine.sales_line }}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </div>
            <div v-else class="text-muted p-2">
              {{ $t('sandoz.select_year_first') }}
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col cols="auto">
            <b-button
              variant="primary"
              @click="loadData"
              :disabled="loading || !canLoad"
            >
              <fa icon="spinner" spin v-if="loading" class="mr-1" />
              <fa icon="search" v-else class="mr-1" />
              {{ $t('core.load') }}
            </b-button>
          </b-col>
          <b-col cols="auto" v-if="productMasters.length > 0">
            <b-button variant="success" @click="saveAll" :disabled="loading || !hasChanges">
              <fa icon="save" class="mr-1" />
              {{ $t('core.save') }}
              <b-badge v-if="hasChanges" variant="light" class="ml-1">{{ changes.size }}</b-badge>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div v-if="loading && productMasters.length === 0" class="text-center py-5">
        <fa icon="spinner" spin size="2x" />
        <p class="mt-2">{{ $t('sandoz.loading_product_master_matrix') }}</p>
      </div>

      <div v-else-if="productMasters.length > 0" class="table-responsive">
        <table class="table table-bordered table-sm product-master-matrix">
          <thead>
            <tr>
              <th rowspan="2" style="min-width: 300px;">{{ $t('sandoz.product') }}</th>
              <th rowspan="2" style="min-width: 140px;">{{ $t('sandoz.contract_types') }}</th>
              <th colspan="2" class="text-center">{{ $t('sandoz.delivery_settings') }}</th>
              <th colspan="2" class="text-center">{{ $t('sandoz.verification_settings') }}</th>
              <th rowspan="2" class="text-center" style="min-width: 90px;">{{ $t('sandoz.sort_order') }}</th>
            </tr>
            <tr>
              <th class="text-center" style="min-width: 120px;">{{ $t('sandoz.delivery') }}</th>
              <th class="text-center" style="min-width: 160px;">{{ $t('sandoz.delivery_mode') }}</th>
              <th class="text-center" style="min-width: 110px;">{{ $t('sandoz.needs_verification') }}</th>
              <th class="text-center" style="min-width: 170px;">{{ $t('sandoz.verification_requirement') }}</th>
            </tr>
          </thead>
          <tbody>
            <product-master-matrix-row
              v-for="product in productMasters"
              :key="product.id"
              :product="product"
              :sales-lines="salesLines"
              :level="0"
              @update="handleUpdate"
            />
          </tbody>
        </table>
      </div>

      <div v-else-if="!loading" class="text-center py-5 text-muted">
        {{ $t('sandoz.click_load_to_view_matrix') }}
      </div>
    </o-card>
  </div>
</template>

<script>
import axios from 'axios'
import { contractYears } from '~/utils/contractYear'
import ProductMasterMatrixRow from './components/ProductMasterMatrixRow'

export default {
  name: 'ProductMasterMatrix',
  components: {
    ProductMasterMatrixRow
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      years: contractYears(),
      loading: false,
      productMasters: [],
      salesLines: [],
      allSalesLines: [],
      selectedContractTypes: [],
      changes: new Map(),
      hasChanges: false
    }
  },
  computed: {
    availableSalesLines() {
      // master has no years per sales line yet (season-2027 filters by them): offer every sales line.
      return this.allSalesLines
    },
    allContractTypesSelected: {
      get() {
        return this.availableSalesLines.length > 0 && this.selectedContractTypes.length === this.availableSalesLines.length
      },
      set() {
        // handled by toggleAllContractTypes
      }
    },
    canLoad() {
      return this.selectedYear && this.availableSalesLines.length > 0 && this.selectedContractTypes.length > 0
    }
  },
  mounted() {
    this.loadAvailableSalesLines()
  },
  methods: {
    loadAvailableSalesLines() {
      axios.get('/api/sandoz/sales_lines?per_page=9999&sortBy=sales_line&sortByDirection=asc')
        .then(response => {
          this.allSalesLines = response.data.data || response.data || []
        })
        .catch(error => {
          console.error('Error loading sales lines:', error)
        })
    },
    clearData() {
      this.productMasters = []
      this.salesLines = []
      this.hasChanges = false
      this.changes.clear()
    },
    onYearChange() {
      this.clearData()
      const validIds = new Set(this.availableSalesLines.map(sl => sl.id))
      this.selectedContractTypes = this.selectedContractTypes.filter(id => validIds.has(id))
    },
    toggleAllContractTypes() {
      if (this.allContractTypesSelected) {
        this.selectedContractTypes = []
      } else {
        this.selectedContractTypes = this.availableSalesLines.map(sl => sl.id)
      }
    },
    fetchMatrix() {
      const params = {
        year: this.selectedYear,
        sales_line_ids: this.selectedContractTypes.join(',')
      }
      return axios.get('/api/sandoz/product_master_matrix', { params })
        .then(response => {
          this.productMasters = response.data.product_masters
          this.salesLines = response.data.sales_lines
        })
    },
    loadData() {
      if (!this.canLoad) {
        return
      }
      if (this.hasChanges && !window.confirm(this.$t('sandoz.unsaved_changes_reload_confirm'))) {
        return
      }

      this.loading = true
      this.hasChanges = false
      this.changes.clear()

      return this.fetchMatrix()
        .catch(error => {
          console.error('Error loading product master matrix:', error)
          this.$bvToast.toast(this.$t('core.error_loading_data'), {
            title: this.$t('core.error'),
            variant: 'danger',
            solid: true
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleUpdate(productMasterId, data) {
      if (data && Object.keys(data).length > 0) {
        this.changes.set(productMasterId, { id: productMasterId, ...data })
      } else {
        this.changes.delete(productMasterId)
      }
      this.hasChanges = this.changes.size > 0
    },
    saveAll() {
      if (this.changes.size === 0) {
        return
      }

      this.loading = true
      const payload = { product_masters: Array.from(this.changes.values()) }

      axios.post('/api/sandoz/product_master_matrix', payload)
        .then(() => {
          this.$bvToast.toast(this.$t('core.data_saved'), {
            title: this.$t('core.success'),
            variant: 'success',
            solid: true
          })
          this.changes.clear()
          this.hasChanges = false
          return this.fetchMatrix()
            .catch(error => {
              console.error('Error reloading product master matrix:', error)
            })
        })
        .catch(error => {
          console.error('Error saving changes:', error)
          this.$bvToast.toast(this.$t('core.error_saving_data'), {
            title: this.$t('core.error'),
            variant: 'danger',
            solid: true
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}
.product-master-matrix thead tr:nth-child(2) th {
  top: 33px;
}
</style>
